<div class="vendor-page-shell p-3 p-lg-4">
  <button class="btn btn-light vendor-back-btn mb-3" (click)="router.navigateByUrl(isVendorUser() ? '/' : '/vendors')">
    <lucide-icon [img]="ArrowLeftIcon" [size]="16"></lucide-icon>
    <span class="ms-2">{{ isVendorUser() ? 'Back to Dashboard' : 'Back to Vendor List' }}</span>
  </button>

  @if (!vendor()) {
    <div class="card card-lift">
      <div class="card-body text-center py-5">Vendor not found</div>
    </div>
  } @else {
    <section class="vendor-hero card card-lift mb-4">
      <div class="card-body">
        <div class="row g-4 align-items-start">
          <div class="col-12 col-xl-7">
            <div class="d-flex align-items-start gap-3">
              <span class="vendor-hero__badge">
                <lucide-icon [img]="Building2Icon" [size]="22"></lucide-icon>
              </span>
              <div class="min-w-0">
                <div class="vendor-hero__eyebrow">Vendor Workspace</div>
                <h1 class="vendor-hero__title mb-1">{{ vendor()?.vendor_name }}</h1>
                <p class="vendor-hero__subtitle mb-3">
                  Review company profile, document compliance, and your own login details from one place.
                </p>
                <div class="d-flex flex-wrap gap-2">
                  <span class="badge vendor-chip">{{ vendor()?.vendor_type }}</span>
                  <span
                    class="badge text-capitalize"
                    [class.text-bg-success]="vendor()?.status === 'approved'"
                    [class.text-bg-warning]="vendor()?.status === 'pending'"
                    [class.text-bg-danger]="vendor()?.status === 'rejected'"
                  >
                    {{ vendor()?.status }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="col-12 col-xl-5">
            <div class="vendor-hero__metrics">
              @for (item of profileHighlights(); track item.label) {
                <div class="vendor-stat-card">
                  <div class="vendor-stat-card__label">{{ item.label }}</div>
                  <div class="vendor-stat-card__value">{{ item.value }}</div>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="row g-4">
      <div class="col-12 col-xl-8">
        <section class="card card-lift vendor-section-card h-100">
          <div class="card-body">
            <div class="section-head">
              <div>
                <div class="section-kicker">Company Profile</div>
                <h2 class="section-title">Vendor Details</h2>
              </div>
              
            </div>

            <div class="profile-grid">
              @for (item of profileRows(); track item.label) {
                <div class="profile-card">
                  <div class="profile-card__label">{{ item.label }}</div>
                  <div class="profile-card__value">{{ item.value }}</div>
                </div>
              }
            </div>
          </div>
        </section>
      </div>

      <div class="col-12 col-xl-4">
        <section class="card card-lift vendor-section-card h-100">
          <div class="card-body">
            <div class="section-head">
              <div>
                <div class="section-kicker">User Access</div>
                <h2 class="section-title">Your User Details</h2>
              </div>
            </div>

            <div class="user-summary-row">
              <div class="user-summary-pill">
                <span>Total Users</span>
                <strong>{{ userStats().total }}</strong>
              </div>
              <div class="user-summary-pill">
                <span>Primary Users</span>
                <strong>{{ userStats().primary }}</strong>
              </div>
            </div>

            @if (users().length === 0) {
              <div class="empty-card">No vendor users found.</div>
            } @else {
              <div class="user-card-list">
                @for (user of users(); track user.id) {
                  <article class="user-card" [class.user-card--editable]="user.id === editableUserId()">
                    <div class="user-card__top">
                      <div class="user-card__identity">
                        <span class="user-card__avatar">
                          <lucide-icon [img]="UserIcon" [size]="16"></lucide-icon>
                        </span>
                        <div class="min-w-0">
                          <div class="user-card__name">{{ user.name }}</div>
                          <div class="user-card__username">{{ user.username }}</div>
                        </div>
                      </div>
                      <div class="d-flex flex-wrap gap-2">
                        @if (user.isPrimary) {
                          <span class="badge text-bg-light">Primary</span>
                        }
                        <span class="badge text-capitalize" [class.text-bg-success]="user.status === 'active'" [class.text-bg-secondary]="user.status !== 'active'">
                          {{ user.status }}
                        </span>
                        @if (user.id === editableUserId()) {
                          <span class="badge text-bg-primary">You</span>
                        }
                      </div>
                    </div>

                    <div class="user-card__meta">
                      <div class="user-meta-pill">
                        <lucide-icon [img]="ShieldUserIcon" [size]="14"></lucide-icon>
                        <span>{{ user.department }} / {{ user.designation }}</span>
                      </div>
                      <div class="user-meta-pill">
                        <lucide-icon [img]="MailIcon" [size]="14"></lucide-icon>
                        <span>{{ user.email }}</span>
                      </div>
                      <div class="user-meta-pill">
                        <lucide-icon [img]="PhoneIcon" [size]="14"></lucide-icon>
                        <span>{{ user.contactNumber || '-' }}</span>
                      </div>
                    </div>

                    @if (user.id === editableUserId()) {
                      <div class="user-card__footer">
                        <button
                          class="status-toggle-btn"
                          type="button"
                          role="switch"
                          [attr.aria-checked]="user.status === 'active'"
                          [class.status-toggle-btn--active]="user.status === 'active'"
                          [disabled]="savingUser()"
                          (click)="toggleOwnUserStatus()"
                        >
                          <span class="status-toggle-btn__track">
                            <span class="status-toggle-btn__thumb"></span>
                          </span>
                          <span class="status-toggle-btn__label">
                            {{ user.status === 'active' ? 'Active' : 'Deactive' }}
                          </span>
                        </button>
                      </div>
                    }
                  </article>
                }
              </div>
            }
          </div>
        </section>
      </div>

      <div class="col-12">
        <section class="card card-lift vendor-section-card">
          <div class="card-body">
            <div class="section-head">
              <div>
                <div class="section-kicker">Files</div>
                <h2 class="section-title">Uploaded Documents</h2>
              </div>
            </div>

            <div class="table-responsive uploaded-doc-table">
              <table class="table align-middle mb-0">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Document Type</th>
                    <th>File Name</th>
                    <th>Size</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  @if (documents().length === 0) {
                    <tr>
                      <td colspan="5" class="text-center py-5 text-muted">
                        <lucide-icon [img]="FileIcon" [size]="20"></lucide-icon>
                        <div class="mt-2">No documents uploaded yet.</div>
                      </td>
                    </tr>
                  }
                  @for (doc of documents(); track doc.id; let i = $index) {
                    <tr>
                      <td>{{ i + 1 }}</td>
                      <td><span class="badge text-bg-light">{{ doc.document_type }}</span></td>
                      <td class="fw-medium">{{ doc.original_name }}</td>
                      <td>{{ formatFileSize(doc.file_size) }}</td>
                      <td>
                        <button class="btn btn-sm btn-light" (click)="download(doc.id)">
                          <lucide-icon [img]="DownloadIcon" [size]="14"></lucide-icon>
                        </button>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  }
</div>
